import { CarrierIssueType } from "@freightsimple/generated-carriers-openapi-client/models/CarrierIssueType";

/** One thing we ask a carrier for when they respond to an issue. */
export type IssueField =
  | "pickupConfirmationNumber"
  | "pickupDate"
  | "expectedDeliveryDate"
  | "currentStatus"
  | "currentLocation"
  | "freightFound"
  | "reason";

export interface IssueQuestion {
  /** What we need, said in the carrier's language. Shown in the banner. */
  prompt: string;
  /** The button that opens the response form. */
  action: string;
  /** The fields the form offers, in order. */
  fields: IssueField[];
  /**
   * The fields an answer must carry for the issue to close.
   *
   * The backend decides this for real — it owns the data and is the only side
   * that can be trusted. This copy is here so the form can tell the carrier
   * what the issue is asking for, not to gate the submission.
   */
  required: IssueField[];
}

const QUESTIONS: Partial<Record<CarrierIssueType, IssueQuestion>> = {
  [CarrierIssueType.BookPickup]: {
    prompt:
      "We need your pickup confirmation number and the date you'll collect.",
    action: "Confirm Pickup",
    fields: ["pickupConfirmationNumber", "pickupDate"],
    required: ["pickupConfirmationNumber", "pickupDate"],
  },
  [CarrierIssueType.MissedPickup]: {
    prompt: "The freight wasn't collected. When will you pick it up?",
    action: "Reschedule Pickup",
    fields: ["pickupDate", "reason"],
    required: ["pickupDate"],
  },
  [CarrierIssueType.ExpectedDeliveryIsInThePast]: {
    prompt: "This is past its delivery date. When will it deliver?",
    action: "Update Delivery Date",
    fields: ["expectedDeliveryDate", "reason"],
    required: ["expectedDeliveryDate"],
  },
  [CarrierIssueType.MissedDelivery]: {
    prompt: "The delivery was missed. When will it deliver?",
    action: "Reschedule Delivery",
    fields: ["expectedDeliveryDate", "reason"],
    required: ["expectedDeliveryDate"],
  },
  [CarrierIssueType.TrackingNotUpdated]: {
    prompt: "Tracking hasn't moved. Where is the freight now?",
    action: "Post an Update",
    fields: ["currentStatus", "currentLocation"],
    required: ["currentStatus"],
  },
  [CarrierIssueType.MissingFreight]: {
    prompt: "We can't locate this freight. Tell us as soon as you find it.",
    action: "Update the Search",
    fields: ["freightFound", "reason"],
    required: ["freightFound"],
  },
  // Customs holds are watched, not worked — there's nothing for the carrier to
  // submit, so the banner is a notice with no action.
};

export function questionFor(type: CarrierIssueType): IssueQuestion | undefined {
  return QUESTIONS[type];
}

/** Labels for the response form's fields. */
export const ISSUE_FIELD_LABELS: Record<IssueField, string> = {
  pickupConfirmationNumber: "Pickup Confirmation Number",
  pickupDate: "Pickup Date",
  expectedDeliveryDate: "Expected Delivery Date",
  currentStatus: "Current Status",
  currentLocation: "Current Location",
  freightFound: "Has the freight been found?",
  reason: "What happened?",
};
